import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Leaf, Menu, User, LogOut } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../lib/AuthContext";

interface HeaderProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}

export function Header({ currentPage = "home", onNavigate }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, profile, signOut } = useAuth();

  const handleNavigation = (page: string, hash?: string) => {
    if (onNavigate) {
      onNavigate(page);
      if (hash) {
        setTimeout(() => {
          const element = document.querySelector(hash);
          element?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
    setMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    await signOut();
    handleNavigation("home");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <button 
              onClick={() => handleNavigation("home")}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              aria-label="CannaBias home"
            >
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center" aria-hidden="true">
                <Leaf className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="text-xl tracking-tight">CannaBias</span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav id="main-navigation" className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            <button 
              onClick={() => handleNavigation("faq")}
              className={`transition-colors ${currentPage === "faq" ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              FAQ
            </button>
            <button 
              onClick={() => handleNavigation("discover")}
              className={`transition-colors ${currentPage === "discover" ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Discover
            </button>
            <button 
              onClick={() => handleNavigation("clinics")}
              className={`transition-colors ${currentPage === "clinics" ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Clinics
            </button>
            <button 
              onClick={() => handleNavigation("products")}
              className={`transition-colors ${currentPage === "products" ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Products
            </button>
            <button 
              onClick={() => handleNavigation("privacy")}
              className={`transition-colors ${currentPage === "privacy" ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Privacy
            </button>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <Badge variant="secondary" className="gap-1.5 pl-2 pr-3 py-1.5 border-primary/20">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  <User className="w-3.5 h-3.5" />
                  <span className="max-w-[120px] truncate">
                    {profile?.display_name || user.email?.split('@')[0]}
                  </span>
                </Badge>
                {/* Only show Account button for non-clinic users */}
                {profile?.role !== 'clinic' && (
                  <Button variant="ghost" onClick={() => handleNavigation("account")}>
                    <User className="w-4 h-4 mr-2" />
                    Account
                  </Button>
                )}
                {profile?.role === 'clinic' && (
                  <Button variant="default" onClick={() => handleNavigation("clinic-dashboard")} className="gradient-green">
                    Clinic Dashboard
                  </Button>
                )}
                <Button variant="outline" onClick={handleSignOut}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => handleNavigation("signin")}>Sign In</Button>
                <Button onClick={() => handleNavigation("signup")}>Get Started</Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <Menu className="w-6 h-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div id="mobile-menu" className="md:hidden border-t border-border bg-background">
          <nav className="px-4 py-6 space-y-4" aria-label="Mobile navigation">
            <button 
              onClick={() => handleNavigation("faq")}
              className="block text-muted-foreground hover:text-foreground transition-colors w-full text-left"
            >
              FAQ
            </button>
            <button 
              onClick={() => handleNavigation("discover")}
              className="block text-muted-foreground hover:text-foreground transition-colors w-full text-left"
            >
              Discover
            </button>
            <button 
              onClick={() => handleNavigation("clinics")}
              className="block text-muted-foreground hover:text-foreground transition-colors w-full text-left"
            >
              Clinics
            </button>
            <button 
              onClick={() => handleNavigation("products")}
              className="block text-muted-foreground hover:text-foreground transition-colors w-full text-left"
            >
              Products
            </button>
            <button 
              onClick={() => handleNavigation("privacy")}
              className="block text-muted-foreground hover:text-foreground transition-colors w-full text-left"
            >
              Privacy
            </button>
            <div className="flex flex-col gap-3 pt-4 border-t border-border">
              {user ? (
                <>
                  {/* Logged in status indicator */}
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
                      </span>
                      <span className="text-sm text-primary font-medium">Logged in as</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <User className="w-4 h-4 text-primary" />
                      <span className="text-sm truncate">
                        {profile?.display_name || user.email}
                      </span>
                    </div>
                  </div>
                  
                  
                  {profile?.role === 'clinic' && (
                    <>
                      <Badge variant="default" className="gap-1 bg-primary justify-center py-1.5">
                        🏥 Clinic Account
                      </Badge>
                      <Button variant="default" className="w-full gradient-green" onClick={() => handleNavigation("clinic-dashboard")}>
                        Clinic Dashboard
                      </Button>
                    </>
                  )}
                  
                  {/* Only show Account Settings for non-clinic users */}
                  {profile?.role !== 'clinic' && (
                    <Button variant="ghost" className="w-full" onClick={() => handleNavigation("account")}>
                      <User className="w-4 h-4 mr-2" />
                      Account Settings
                    </Button>
                  )}
                  <Button variant="outline" className="w-full" onClick={handleSignOut}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" className="w-full" onClick={() => handleNavigation("signin")}>
                    Sign In
                  </Button>
                  <Button className="w-full" onClick={() => handleNavigation("signup")}>
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}